import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { hasKey } from '~/utils';

export type CreateIntakeParams = {
  programId: string;
  campusId: string;
  intakeTermId: string;
  firstDayOfClassOn: string;
  lastDayOfClassOn: string;
  applicationFee?: number;
  acceptingAppsStartsAt?: string;
  acceptingAppsClosesAt?: string;
  deferralDeadlineAt?: string;
  refundDeadlineAt?: string;
  tuition?: number;
};

type CreateIntakeResponse = {
  data: {
    id: string;
    type: string;
    attributes: Record<string, unknown>;
  };
};

export function useCreateIntake() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation(['createIntake'], async (payload: CreateIntakeParams) => {
    const response = await request<CreateIntakeResponse>(`${config.apiHost}/private/v1/intakes`, {
      method: 'POST',
      body: {
        data: {
          type: 'intake',
          attributes: {
            programId: payload.programId,
            campusId: payload.campusId,
            intakeTermId: payload.intakeTermId,
            firstDayOfClassOn: payload.firstDayOfClassOn,
            lastDayOfClassOn: payload.lastDayOfClassOn,
            applicationFee: payload.applicationFee,
            acceptingAppsStartsAt: payload.acceptingAppsStartsAt,
            acceptingAppsClosesAt: payload.acceptingAppsClosesAt,
            deferralDeadlineAt: payload.deferralDeadlineAt,
            refundDeadlineAt: payload.refundDeadlineAt,
            tuition: payload.tuition,
          },
        },
      },
      isExpectedResponse,
    });

    await queryClient.invalidateQueries(['intakes']);
    await queryClient.invalidateQueries(['useIntakeManagementPageProps']);

    return response;
  });

  return {
    createIntake: mutateAsync,
    createIntakeError: error,
    isCreatingIntake: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is CreateIntakeResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
